import {
    Button,
    Card,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { useParams } from 'react-router-dom';
import { RootState } from '../../../reducers';
import { useEffect, useState } from 'react';
import { DataGrid, gridClasses, GridColDef } from '@mui/x-data-grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { isValid } from 'date-fns';
import { toDate, formatDate } from '../../../utils/dateHelpers';
import { copyObject, isDefaultDate } from '../../../utils/common';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { SingleNeed } from '../../../Models/CurrentNeedsModel/SingleNeed.model';
import { NO, YES } from '../../../utils/constants';
import {
    fetchCurrentNeedOptions,
    fetchCurrentNeedsByMemberId,
    handleConfirmAddNeed,
    handleConfirmDeleteNeed,
    handleConfirmEditNeed,
    saveNeeds,
    setCurrentNeedsInfo,
    setModalType,
    setSelectedNeed,
} from '../../../store/currentNeedsSlice.slice';
import AddIcon from '@mui/icons-material/Add';
import { SingleNeedComment } from '../../../Models/CurrentNeedsModel/SingleNeedComment.model';

const SharedModal = (props: any) => {
    const { body, disabled = false, open, onClose, onConfirm, title } = props;
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>{body}</DialogContent>
            <DialogActions>
                <Button sx={{ minWidth: '120px' }} variant="outlined" color="primary" onClick={onClose}>
                    Cancel
                </Button>
                <Button disabled={disabled} sx={{ minWidth: '120px' }} variant="contained" color="primary" onClick={onConfirm}>
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    );
};
const ADD = 'Add';
const EDIT = 'Edit';
const DELETE = 'Delete';
const OTHER = 'Other addictive behaviors';
const yesNoList = [{ Answer: YES }, { Answer: NO }];

const CurrentNeedsMemberDetail = (props: any) => {
    const params = useParams();
    const memberId = props.memberId || params.memberId;
    const dispatch = useAppDispatch();
    const { currentNeedsInfo, currentNeedsOptions, modalType, selectedNeed } = useSelector((state: RootState) => state.currentNeedsSlice);
    const selectedNeedIsValid =
        selectedNeed.OptionLabel !== '-1' && (selectedNeed.IsMet === NO || (selectedNeed.IsMet === YES && selectedNeed.HowNeedWasMet !== ''));
    const [editingCommentIndex, setEditingCommentIndex] = useState<number | null>(null);
    const [editCommentText, setEditCommentText] = useState('');
    const [deletingCommentIndex, setDeletingCommentIndex] = useState<number | null>(null);

    const columns: GridColDef[] = [
        {
            field: 'OptionLabel',
            headerName: 'Need',
            minWidth: 160,
            valueGetter: (value, row) => {
                return row.OptionLabel;
            },
        },
        {
            field: 'IsMet',
            headerName: 'Need Met',
            minWidth: 80,
            renderCell: (params) => {
                return params.row.IsMet === 'Yes' ? <CheckCircleIcon color="success" /> : <CloseIcon color="error" />;
            },
        },
        {
            field: 'HowNeedWasMet',
            headerName: 'How Was Need Met',
            minWidth: 250,
            flex: 1,
            valueGetter: (value, row) => {
                return row.HowNeedWasMet ? row.HowNeedWasMet : '-';
            },
        },
        {
            field: 'Comments',
            headerName: 'Comments',
            minWidth: 375,
            flex: 1.25,
            renderCell: (params) => {
                return params.row.NeedComments?.length > 0 ? (
                    <List
                        sx={{
                            width: '100%',
                            bgcolor: 'background.paper',
                            maxHeight: 150,
                            overflowY: 'auto',
                        }}
                        dense={true}
                        subheader={<li />}
                    >
                        {params.row.NeedComments.map((comment: any, index: number) => {
                            return (
                                <ListItem key={`${comment.Comment}-${index}`} divider={index !== params.row.NeedComments.length - 1}>
                                    <Tooltip
                                        title={
                                            <Stack>
                                                <Typography variant="body2">{formatDate(comment.CreatedDate, 'MM/dd/yyyy hh:mm a')}</Typography>
                                                <Typography variant="body2" fontStyle={'italic'}>
                                                    {comment.CreatedBy?.Name}
                                                </Typography>
                                            </Stack>
                                        }
                                        placement="top"
                                    >
                                        <ListItemText primary={comment.Comment} />
                                    </Tooltip>
                                </ListItem>
                            );
                        })}
                    </List>
                ) : (
                    '-'
                );
            },
        },
        {
            field: 'UpdatedDate',
            headerName: 'Last Updated',
            minWidth: 160,
            renderCell: (params) => {
                return (
                    <Stack>
                        <Typography variant="body2" color="text.secondary">
                            {isDefaultDate(params.row.UpdatedDate) ? '-' : formatDate(params.row.UpdatedDate, 'MM/dd/yyyy hh:mm a')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontStyle={'italic'}>
                            {params.row.UpdatedBy?.Name}
                        </Typography>
                    </Stack>
                );
            },
        },
        {
            field: 'CreatedDate',
            headerName: 'Created On',
            minWidth: 160,
            renderCell: (params) => {
                return (
                    <Stack>
                        <Typography variant="body2" color="text.secondary">
                            {isDefaultDate(params.row.CreatedDate) ? '-' : formatDate(params.row.CreatedDate, 'MM/dd/yyyy hh:mm a')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontStyle={'italic'}>
                            {params.row.CreatedBy?.Name}
                        </Typography>
                    </Stack>
                );
            },
        },
        {
            field: 'action',
            headerName: 'Action',
            sortable: false,
            minWidth: 160,
            renderCell: (params) => {
                return (
                    <>
                        <Tooltip title="Edit" placement="left">
                            <EditIcon color="primary" style={{ marginLeft: '24px', cursor: 'pointer' }} onClick={() => handleEditNeed(params.row)} />
                        </Tooltip>
                        <Tooltip title="Delete" placement="right">
                            <CancelIcon color="error" style={{ marginLeft: '24px', cursor: 'pointer' }} onClick={() => handleDeleteNeed(params.row)} />
                        </Tooltip>
                    </>
                );
            },
        },
    ];

    const handleAddNeed = () => {
        const newNeed = new SingleNeed();
        newNeed.OptionLabel = '-1';
        dispatch(setSelectedNeed(newNeed));
        dispatch(setModalType(ADD));
    };

    const handleEditNeed = (row: any) => {
        dispatch(setSelectedNeed(row));
        dispatch(setModalType(EDIT));
    };

    const handleDeleteNeed = (row: any) => {
        dispatch(setSelectedNeed(row));
        dispatch(setModalType(DELETE));
    };

    const handleAddNeedComment = (singleNeed: SingleNeed) => {
        const copy = copyObject(selectedNeed);
        const newComment = new SingleNeedComment();
        newComment.Comment = singleNeed.Comments;
        if (copy.NeedComments) {
            copy.NeedComments = [newComment, ...copy.NeedComments];
        } else {
            copy.NeedComments = [newComment];
        }
        copy.Comments = '';
        dispatch(setSelectedNeed(copy));
    };

    const handleConfirmEditNeedComment = () => {
        if (editingCommentIndex === null || !editCommentText.trim()) {
            return;
        }
        const copy = copyObject(selectedNeed);
        if (copy.NeedComments?.[editingCommentIndex]) {
            copy.NeedComments[editingCommentIndex].Comment = editCommentText.trim();
            dispatch(setSelectedNeed(copy));
        }
        setEditingCommentIndex(null);
        setEditCommentText('');
    };

    const handleConfirmDeleteNeedComment = () => {
        if (deletingCommentIndex === null) {
            return;
        }
        const copy = copyObject(selectedNeed);
        copy.NeedComments = (copy.NeedComments || []).filter((_, i) => i !== deletingCommentIndex);
        dispatch(setSelectedNeed(copy));
        setDeletingCommentIndex(null);
    };

    const handleSaveNeeds = (currentNeedsInfo: any) => {
        dispatch(saveNeeds(currentNeedsInfo));
    };

    const editNeedBody = (selectedNeed: SingleNeed) => {
        return (
            <Stack spacing={2} sx={{ paddingTop: 2 }}>
                <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label" required>
                        Selected Need
                    </InputLabel>
                    <Select
                        label="Selected Need"
                        labelId="demo-simple-select-label"
                        variant="outlined"
                        size="small"
                        required
                        disabled={modalType === EDIT}
                        fullWidth
                        value={selectedNeed?.OptionLabel}
                        onChange={(e) => {
                            dispatch(
                                setSelectedNeed({
                                    ...selectedNeed,
                                    OptionLabel: e.target.value,
                                })
                            );
                        }}
                        error={selectedNeed?.OptionLabel === '-1'}
                    >
                        <MenuItem value={'-1'} disabled>
                            Select Need
                        </MenuItem>
                        {currentNeedsOptions.map((item, index) => (
                            <MenuItem key={`${item.OptionLabel}_${index}`} value={item.OptionLabel}>
                                {item.OptionLabel}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormLabel error={selectedNeed?.OptionLabel === '-1'} sx={{ fontSize: '12px', color: '#d32f2f' }}>
                        {selectedNeed?.OptionLabel === '-1' ? 'This field is required' : ''}
                    </FormLabel>
                </FormControl>
                <Stack direction={'row'} spacing={2} alignItems="center" pl={1}>
                    <FormLabel required>Is this need met?</FormLabel>
                    <RadioGroup
                        row
                        aria-labelledby="demo-row-radio-buttons-group-label"
                        name="IsMet"
                        value={selectedNeed?.IsMet}
                        onChange={(e) => {
                            dispatch(
                                setSelectedNeed({
                                    ...selectedNeed,
                                    IsMet: e.target.value,
                                    HowNeedWasMet: '',
                                })
                            );
                        }}
                    >
                        {yesNoList.map((item) => (
                            <FormControlLabel key={item.Answer} value={item.Answer} control={<Radio />} label={item.Answer} />
                        ))}
                    </RadioGroup>
                </Stack>
                <Collapse in={selectedNeed?.IsMet === YES}>
                    <TextField
                        label="How was this need met?"
                        variant="outlined"
                        size="small"
                        required={selectedNeed?.IsMet === YES}
                        fullWidth
                        multiline
                        rows={3}
                        margin="normal"
                        name="HowNeedWasMet"
                        value={selectedNeed?.HowNeedWasMet}
                        onChange={(e) => {
                            dispatch(
                                setSelectedNeed({
                                    ...selectedNeed,
                                    HowNeedWasMet: e.target.value,
                                })
                            );
                        }}
                        error={selectedNeed?.IsMet === YES && !selectedNeed?.HowNeedWasMet}
                        helperText={selectedNeed?.IsMet === YES && !selectedNeed?.HowNeedWasMet ? 'This field is required' : ''}
                    />
                </Collapse>

                <Stack direction={'row'} spacing={2} alignItems="center" pl={1}>
                    <TextField
                        label="Comments"
                        variant="outlined"
                        size="small"
                        fullWidth
                        name="Comments"
                        placeholder="Please provide any additional comments"
                        value={selectedNeed?.Comments || ''}
                        onChange={(e) => {
                            dispatch(
                                setSelectedNeed({
                                    ...selectedNeed,
                                    Comments: e.target.value,
                                })
                            );
                        }}
                    />
                    <Button
                        variant="contained"
                        disabled={selectedNeed?.Comments?.length === 0}
                        onClick={() => {
                            handleAddNeedComment(selectedNeed);
                        }}
                    >
                        <AddIcon />
                    </Button>
                </Stack>
                {selectedNeed?.NeedComments?.length > 0 &&
                    selectedNeed?.NeedComments.map((comment, index) => {
                        return (
                            <Tooltip
                                key={comment.Id || `${comment.Comment}-${index}`}
                                title={
                                    <Stack>
                                        <Typography variant="body2">{formatDate(comment.CreatedDate, 'MM/dd/yyyy hh:mm a')}</Typography>
                                        <Typography variant="body2" fontStyle={'italic'}>
                                            {comment.CreatedBy?.Name}
                                        </Typography>
                                    </Stack>
                                }
                                placement="bottom"
                            >
                                <Paper elevation={1} sx={{ padding: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                                        <Typography variant="body1" sx={{ flex: 1 }}>
                                            {comment.Comment}
                                        </Typography>
                                        <Stack direction="row" spacing={0.5}>
                                            <IconButton
                                                size="small"
                                                title="Edit comment"
                                                onClick={() => {
                                                    setEditingCommentIndex(index);
                                                    setEditCommentText(comment.Comment || '');
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                title="Delete comment"
                                                onClick={() => setDeletingCommentIndex(index)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </Stack>
                                </Paper>
                            </Tooltip>
                        );
                    })}
            </Stack>
        );
    };

    const deleteNeedBody = (selectedNeed: SingleNeed) => {
        return (
            <Stack spacing={2} sx={{ paddingTop: 2 }}>
                <Typography variant="body1">Are you sure you want to delete this need?</Typography>
                <Stack direction={'row'} spacing={2} alignItems="center" pl={1}>
                    <FormLabel>Need:</FormLabel>
                    <Typography variant="body1" fontWeight={'bold'}>
                        {selectedNeed?.OptionLabel === OTHER ? `Other: ${selectedNeed?.OptionLabelOther}` : selectedNeed?.OptionLabel}
                    </Typography>
                </Stack>
                <Stack direction={'row'} spacing={2} alignItems="center" pl={1}>
                    <FormLabel>Created:</FormLabel>
                    <Typography variant="body1" fontWeight={'bold'}>
                        {selectedNeed?.CreatedDate ? formatDate(selectedNeed.CreatedDate, 'MM/dd/yyyy hh:mm a') : '-'}
                    </Typography>
                </Stack>
            </Stack>
        );
    };

    useEffect(() => {
        dispatch(fetchCurrentNeedOptions());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchCurrentNeedsByMemberId(memberId));
    }, [dispatch, memberId]);

    return (
        <Box sx={{ padding: 2 }}>
            <SharedModal
                disabled={!selectedNeedIsValid}
                open={modalType === ADD}
                title="Add Need"
                body={editNeedBody(selectedNeed)}
                onClose={() => dispatch(setModalType(''))}
                onConfirm={() => dispatch(handleConfirmAddNeed(selectedNeed, memberId))}
            />
            <SharedModal
                open={modalType === DELETE}
                title="Delete Need"
                body={deleteNeedBody(selectedNeed)}
                onClose={() => dispatch(setModalType(''))}
                onConfirm={() => dispatch(handleConfirmDeleteNeed(selectedNeed))}
            />
            <SharedModal
                disabled={!selectedNeedIsValid}
                open={modalType === EDIT}
                title="Edit Need"
                body={editNeedBody(selectedNeed)}
                onClose={() => dispatch(setModalType(''))}
                onConfirm={() => dispatch(handleConfirmEditNeed(selectedNeed))}
            />

            <Dialog open={editingCommentIndex !== null} onClose={() => setEditingCommentIndex(null)} fullWidth maxWidth="sm">
                <DialogTitle>Edit Comment</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Comment"
                        fullWidth
                        multiline
                        rows={4}
                        value={editCommentText}
                        onChange={(e) => setEditCommentText(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setEditingCommentIndex(null)}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleConfirmEditNeedComment} disabled={!editCommentText.trim()}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deletingCommentIndex !== null} onClose={() => setDeletingCommentIndex(null)} fullWidth maxWidth="xs">
                <DialogTitle>Delete Comment</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this comment?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setDeletingCommentIndex(null)}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="error" onClick={handleConfirmDeleteNeedComment}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Grid container spacing={2} pb={4}>
                <Grid size={12}>
                    <Stack direction="row" alignItems="center" mt={2}>
                        <strong>Last Reviewed By : </strong>
                        {Boolean(currentNeedsInfo?.UpdatedBy) ? (
                            <Chip size="small" label={`${currentNeedsInfo.UpdatedBy?.Name} - ${(() => { const d = toDate(currentNeedsInfo.UpdatedDate); return d ? formatDate(d, 'MM/dd/yyyy') : ''; })()}`} />
                        ) : (
                            ' - '
                        )}
                    </Stack>
                </Grid>
                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                    <hr className="hr-lite" />
                </Grid>
                <Grid size={12}>
                    <Card sx={{ padding: 2, marginBottom: 2 }} elevation={3}>
                        <Stack spacing={2}>
                            <Button variant="contained" color="primary" onClick={handleAddNeed} sx={{ width: '120px', float: 'right' }}>
                                Add Need
                            </Button>

                            <DataGrid
                                rows={currentNeedsInfo?.CurrentNeedsList || []}
                                autoHeight
                                columns={columns}
                                disableRowSelectionOnClick
                                hideFooter
                                getRowId={(row) => row.Id}
                                getRowHeight={() => 'auto'}
                                sx={{
                                    [`& .${gridClasses.cell}`]: {
                                        py: 1,
                                    },
                                }}
                            />
                        </Stack>
                    </Card>
                </Grid>
                <Grid size={6}>
                    <FormLabel>Member and/or significant other need education on:</FormLabel>
                </Grid>
                <Grid size={6}>
                    <TextField
                        label="Please specify"
                        variant="outlined"
                        size="small"
                        multiline
                        rows={3}
                        fullWidth
                        margin="normal"
                        name="NeedsEducationOn"
                        value={currentNeedsInfo.NeedsEducationOn || ''}
                        onChange={(e) => {
                            dispatch(setCurrentNeedsInfo({ ...currentNeedsInfo, NeedsEducationOn: e.target.value }));
                        }}
                    />
                </Grid>
                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                    <hr className="hr-lite" />
                </Grid>
                <Grid size={6}>
                    <FormLabel>Member needs other environmental supports:</FormLabel>
                </Grid>
                <Grid size={6}>
                    <TextField
                        label="Please specify"
                        variant="outlined"
                        size="small"
                        multiline
                        rows={3}
                        fullWidth
                        margin="normal"
                        name="NeedsOtherEnvironmentalSupport"
                        value={currentNeedsInfo.NeedsOtherEnvironmentalSupport || ''}
                        onChange={(e) => {
                            dispatch(setCurrentNeedsInfo({ ...currentNeedsInfo, NeedsOtherEnvironmentalSupport: e.target.value }));
                        }}
                    />
                </Grid>
                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                    <hr className="hr-lite" />
                </Grid>
                <Grid size={6}>
                    <FormLabel>Skills deficit/skills training/community support needs:</FormLabel>
                </Grid>
                <Grid size={6}>
                    <TextField
                        label="Please specify"
                        variant="outlined"
                        size="small"
                        multiline
                        rows={3}
                        fullWidth
                        margin="normal"
                        name="SkillsAndSupportsNeeds"
                        value={currentNeedsInfo.SkillsAndSupportsNeeds || ''}
                        onChange={(e) => {
                            dispatch(setCurrentNeedsInfo({ ...currentNeedsInfo, SkillsAndSupportsNeeds: e.target.value }));
                        }}
                    />
                </Grid>
                <Grid size={12}>
                    <Button variant="contained" color="primary" onClick={() => handleSaveNeeds(currentNeedsInfo)} sx={{ float: 'right', minWidth: '120px' }}>
                        Save
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CurrentNeedsMemberDetail;
